import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, ButtonBase, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { RootState } from "../../store/store";
import { Category, CategoryItem } from "../../types";
import { defaultCategory } from "../common/Category";
import request from "../../apis/request";
import { APIResponse } from "../../types/apiTypes";
import { setCategory } from "../../store/appSlice";
import {
  CategoryItemComponent,
  AddCategoryItemComponent,
} from "./CategoryItemComponent";
import { DragDropProvider } from "@dnd-kit/react";
import { useTranslation } from "react-i18next";

const CategoryEdit = () => {
  const { isLoggedIn, category, userInfo } = useSelector(
    (state: RootState) => state.app,
  );
  const dispatch = useDispatch();

  const [draftCategory, setDraftCategory] = useState<Category | null>(category);
  const [newCategoryLabel, setNewCategoryLabel] = useState("");
  const [addingCategoryType, setAddingCategoryType] = useState<
    "income" | "expense" | null
  >(null);
  const [isEdited, setIsEdited] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragStartIdx, setDragStartIdx] = useState(-1);
  const [saveError, setSaveError] = useState("");
  const { t: tCommon } = useTranslation("common");
  const { t: tError } = useTranslation("errors");

  useEffect(() => {
    setDraftCategory(category);
  }, [category]);

  const handleAddCategory = (type: "income" | "expense") => {
    const label = newCategoryLabel.trim();
    if (!label || !draftCategory) return;

    const newItem: CategoryItem = {
      isDefault: false,
      label,
      otherLangs: [{ lang: "ja", label }],
    };

    const newCategory: Category =
      type === "income"
        ? { ...draftCategory, income: [...draftCategory.income, newItem] }
        : { ...draftCategory, expense: [...draftCategory.expense, newItem] };

    setDraftCategory(newCategory);
    handleCancelCategory();
    setIsEdited(true);
  };

  const handleCancelCategory = () => {
    setAddingCategoryType(null);
    setNewCategoryLabel("");
  };

  const handleSaveCategory = async () => {
    if (!isLoggedIn) {
      alert("ログインが必要です。");
      return;
    }
    if (!draftCategory) return;

    setIsProcessing(true);
    setSaveError("");
    try {
      const response = await request<APIResponse<Category>>({
        method: "PUT",
        url: "updateCategory",
        requireAuth: true,
        data: draftCategory,
        token: userInfo?.token,
      });
      if (!response.data.success) {
        throw new Error(response.data.message);
      }

      dispatch(setCategory(draftCategory));
      handleCancelCategory();
      setIsEdited(false);
    } catch (error: any) {
      console.error(`カテゴリ保存に失敗しました。${error.message} `, error);
      alert(`カテゴリ保存に失敗しました。(${error.message})`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteCategoryItem = async (type: string, item: CategoryItem) => {
    console.log("--s0700-delete category-", type, item);
    try {
      setIsProcessing(true);
      let newCategory;
      if (type === "income") {
        const income = category?.income.filter(
          (inc) => inc.label !== item.label,
        );
        newCategory = {
          ...category,
          income,
        } as Category;
      }
      if (type === "expense") {
        const expense = category?.expense.filter(
          (exp) => exp.label !== item.label,
        );
        newCategory = {
          ...category,
          expense,
        } as Category;
      }

      const response = await request<APIResponse<Category>>({
        method: "PUT",
        url: "updateCategory",
        requireAuth: true,
        data: newCategory,
        token: userInfo?.token,
      });
      if (!response.data.success) {
        throw new Error(response.data.message);
      }

      dispatch(setCategory(newCategory as Category));
      handleCancelCategory();
      setIsEdited(false);
    } catch (error: any) {
      console.error(`カテゴリ保存に失敗しました。${error.message} `, error);
      alert(`カテゴリ保存に失敗しました。(${error.message})`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResetEditCategoryChanges = () => {
    setDraftCategory(category);
    setSaveError("");
    handleCancelCategory();
    setIsEdited(false);
  };

  const handleSetDefaultCategory = () => {
    handleCancelCategory(); //setAddingCategoryType(null); setNewCategoryLabel("");
    setDraftCategory(defaultCategory);

    setIsEdited(true);
  };

  const handleDragStart = (event: any, type: string) => {
    const { source, canceled } = event.operation;

    if (canceled) setDragStartIdx(-1);

    setDragStartIdx(source.index);
  };

  const handleDragEnd = (event: any, type: string) => {
    const { target, canceled } = event.operation;

    if (canceled || !dragStartIdx || !target) return;

    const targetItems =
      type === "expense" ? draftCategory?.expense : draftCategory?.income;
    if (!targetItems) return;

    // JSON arrayのitemの移動　method 1
    // const updated = [...targetItems];
    // const [removed] = updated.splice(srcId, 1);
    // updated.splice(movedId, 0, removed);

    // JSON arrayのitemの移動　method 2
    const moveItem = (
      items: CategoryItem[],
      srcIdx: number,
      movedIdx: number,
    ) => {
      const movingItem = items[srcIdx];
      const restItems = items.filter((_, index) => index !== srcIdx);

      return [
        ...restItems.slice(0, movedIdx),
        movingItem,
        ...restItems.slice(movedIdx),
      ];
    };

    const updated = moveItem(targetItems, dragStartIdx, target.index);
    const updatedCategory = {
      ...draftCategory,
      expense: type === "expense" ? updated : draftCategory?.expense,
      income: type === "income" ? updated : draftCategory?.income,
    } as Category;
    setDraftCategory(updatedCategory);

    setIsEdited(true);
  };

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e5e5",
        borderRadius: 2,
        boxShadow: "0 1px 5px rgba(0, 0, 0, 0.12)",
        p: { xs: 1.5, sm: 2 },
      }}
    >
      <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>
        {tCommon("labels.editCategory")}
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(280px, 1fr))",
          gap: 1.5,
          overflowX: "auto",
          pb: 0.5,
        }}
      >
        {(
          [
            {
              title: tCommon("transaction.income"),
              type: "income",
              items: draftCategory?.income,
            },
            {
              title: tCommon("transaction.expense"),
              type: "expense",
              items: draftCategory?.expense,
            },
          ] as const
        ).map(({ title, type, items }) => (
          <Box
            key={title}
            sx={{
              minWidth: 0,
              bgcolor: "#f8f8f8",
              border: "1px solid #e5e5e5",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{ mb: 1.5, fontWeight: 700 }}
            >
              {title}
            </Typography>
            <Stack spacing={1.25}>
              <DragDropProvider
                onDragStart={(e) => handleDragStart(e, type)}
                onDragEnd={(e) => handleDragEnd(e, type)}
              >
                {items?.map((item, index) => (
                  <CategoryItemComponent
                    key={`${index}_` + item.label}
                    type={type}
                    item={item}
                    index={index}
                    categoryLabel={newCategoryLabel}
                    setNewCategoryLabel={setNewCategoryLabel}
                    onAddCategory={handleAddCategory}
                    onCancelCategory={handleCancelCategory}
                    onDeleteCategoryItem={handleDeleteCategoryItem}
                  />
                ))}
              </DragDropProvider>

              {addingCategoryType === type ? (
                //　新規カテゴリ編集中の場合
                <AddCategoryItemComponent
                  type={type}
                  item={{} as CategoryItem}
                  index={999}
                  categoryLabel={newCategoryLabel}
                  setNewCategoryLabel={setNewCategoryLabel}
                  onAddCategory={handleAddCategory}
                  onCancelCategory={handleCancelCategory}
                  onDeleteCategoryItem={handleDeleteCategoryItem}
                />
              ) : (
                <ButtonBase
                  component="button"
                  type="button"
                  onClick={() => {
                    setNewCategoryLabel("");
                    setAddingCategoryType(type);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 0.75,
                    minHeight: 40,
                    px: 1,
                    color: "text.secondary",
                    font: "inherit",
                    fontWeight: 500,
                  }}
                >
                  <AddIcon fontSize="small" />
                  <Typography component="span">
                    {tCommon("actions.addNew")}
                  </Typography>
                </ButtonBase>
              )}
            </Stack>
          </Box>
        ))}
      </Box>
      <Stack
        direction="row"
        sx={{
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mt: 2,
          pt: 1.5,
          borderTop: "1px solid #e5e5e5",
        }}
      >
        {/* {saveError && (
          <Alert severity="error" sx={{ mr: "auto" }}>
            {saveError}
          </Alert>
        )} */}
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={handleSetDefaultCategory}
            disabled={isProcessing}
          >
            {tCommon("actions.toDefault")}
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={handleResetEditCategoryChanges}
            disabled={isProcessing}
          >
            {tCommon("actions.reset")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCategory}
            disabled={
              !draftCategory || isProcessing || !isLoggedIn || !isEdited
            }
          >
            {tCommon("actions.save")}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CategoryEdit;
